// KickedScreen — Phase 7 Task 7-4 dedicated landing for "you have been kicked".
//
// Triggered by `RoomSession.applyServerEvent` transitioning phase →
// `'kicked'` after `room.member-left { reason: 'kick', uid === self.uid }`
// arrives. SessionProvider also clears its reconnect bookkeeping when it
// observes the same event so we don't auto-re-join (and immediately get
// kicked again).
//
// CTA: 同じ部屋にもう一度入室を試す ("再入室") + ホームに戻る ("ホームへ戻る").
// 再入室は pathname `/<id>` URL に戻して hard reload (= location.assign) —
// ReJoinScreen がふたたび表示される. ホームも同様に hard reload で
// SessionProvider transport を作り直す.
//
// 既存 ErrorScreen を流用しなかった理由: kicked 専用 phase なので App.tsx
// の `renderRejected` ではなく direct switch から呼びたい (= 別コンポー
// ネント). ErrorScreen が CTA を 1 個しか持たないので「再入室」と「ホーム」
// 両方を出したい本画面は inline で組む.

import { hardReloadToHome, hardReloadToRoom } from './urlNavigation.js';

export interface KickedScreenProps {
  /** Resolved kicker displayName (or fallback like 「管理者」). */
  readonly kickerDisplayName: string;
  /**
   * Room id to re-attempt entry on. Provided by `phase.kicked.roomId` —
   * stamped from the prior `RoomState` so we can offer a frictionless retry.
   */
  readonly roomId: string;
}

const goHome = (): void => {
  hardReloadToHome();
};

// hard reload (location.assign) so the SessionProvider transport re-mounts
// cleanly — mirroring ReJoinScreen.goToCreateScreen's rationale (a soft
// popstate would reuse the (now-closed) WSClient and confuse the reducer).
const goReJoin = (roomId: string): void => {
  hardReloadToRoom(roomId);
};

export const KickedScreen = ({ kickerDisplayName, roomId }: KickedScreenProps): React.ReactNode => {
  return (
    // p-4 + w-full max-w: スマホ縦 (390/360px) はみ出し対策 (ErrorScreen と同パターン、
    // 2026-07-03 実機 fb)。デスクトップは max-w が同値なので見た目不変。
    <div className="grid h-full place-items-center p-4" role="alert">
      <div className="w-full max-w-[420px] rounded border border-white/10 bg-[var(--color-panel)] p-6 shadow-xl">
        <h1 className="mb-3 text-center text-base font-bold text-[var(--color-error)]">
          キックされました
        </h1>
        <p className="mb-4 text-center text-xs text-[var(--color-text)]/85">
          {kickerDisplayName} によってルームから退場させられました。
        </p>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => goReJoin(roomId)}
            className="w-full rounded bg-[var(--color-accent)] px-4 py-2 text-sm font-bold text-[var(--color-panel)] transition hover:brightness-110"
          >
            もう一度入室する
          </button>
          <button
            type="button"
            onClick={goHome}
            className="w-full rounded border border-white/30 bg-transparent px-4 py-2 text-sm font-bold text-[var(--color-text)] transition hover:bg-white/10"
          >
            ホームへ戻る
          </button>
        </div>
        <p className="mt-3 text-center font-mono text-[10px] text-[var(--color-text-muted)]">
          roomId: {roomId.slice(0, 8)}...
        </p>
      </div>
    </div>
  );
};
